import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { API, FixedAspectRatioImageDTO, RoleDTO, SingleFileUpload, UserClientFullDTO, UserDTO } from 'src/app/services/api/api';
import { UserService } from 'src/app/services/user.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { ImageUploadService } from 'src/app/services/gui/image/image-upload.service';
import { ImageUploadModel } from 'src/app/services/gui/image/model';
import { Clipping } from 'src/app/components/image/model';
import { Mutable } from '../../../services/global/global-interfaces';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: false,
})
export class UserProfileComponent implements OnInit, OnDestroy {
  avatarUrl: string;
  loading: boolean;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  roles: RoleDTO[];
  personalPermissions: string[];
  user: Mutable<UserClientFullDTO>;
  imageUpload: ImageUploadModel;
  iconNames: string[];
  icons: { name: string; pos: { top: any; left: any; transform: any } }[];

  constructor(
    private readonly api: API,
    private readonly imageUploadService: ImageUploadService,
    public readonly modal: ModalService,
    public readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    public readonly userStatus: UserService,
    public readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef
  ) {
    this.avatarUrl = './assets/img/no-profile-man.png';
    this.loading = true;
    this.roles = [];
    this.personalPermissions = [];
    this.user = undefined;
    this.imageUpload = new ImageUploadModel();
    this.iconNames = [
      'age',
      'assignment',
      'attributes',
      'dashboard',
      'camera',
      'colorDefinitions',
      'company',
      'customer',
      'globe',
      'graphModel',
      'hammer',
      'home',
      'legacy',
      'location',
      'masterData',
      'motives',
      'megaphoneTypes',
      'networkInfrastructure',
      'network',
      'playerOnline',
      'product',
      'qrCode',
      'securityCode',
      'selectionSettings',
      'shoppingkart',
      'steleAttributes',
      'tracker',
      'treeTrunk',
      'userRoles',
      'uploadAlt',
    ];

    this.icons = [];
  }

  ngOnInit(): void {
    this.refreshFromServer();
    this.initIcons();
    this.shuffleIcons();
  }

  ngOnDestroy(): void {
    this.imageUpload?.destroySubscriptions();
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .shared_Status_status()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: UserClientFullDTO) => {
          this.user = data;
          this.avatarUrl = this.user.user?.avatar
            ? this.util.appendQueryParamToUrl(this.user.user.avatar.url as unknown as string, 'thumbnail=L')
            : './assets/img/no-profile-man.png';
          this.getPermissionSettings();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  getPermissionSettings() {
    this.api
      .shared_Client_retrievePersonalPermissions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: string[]) => {
          this.personalPermissions = data;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  initIcons() {
    for (const iconName of this.iconNames) {
      this.icons.push({
        name: iconName,
        pos: {
          top: 0,
          left: 0,
          transform: '',
        },
      });
    }
  }

  shuffleIcons() {
    for (let i = this.icons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.icons[i], this.icons[j]] = [this.icons[j], this.icons[i]];
    }

    const count = this.icons.length;
    const increment = 100 / count;
    let top = 0;
    let left = 0;

    for (const icon of this.icons) {
      top += increment;
      let variance = increment;
      variance = variance * Math.random();
      if (Math.random() < 0.5) {
        variance = -variance;
      }
      let iconTop = top;
      iconTop += variance;
      icon.pos.top = iconTop + '%';
    }

    for (let i = this.icons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.icons[i], this.icons[j]] = [this.icons[j], this.icons[i]];
    }

    for (const icon of this.icons) {
      left += increment;
      let variance = increment;
      variance = variance * Math.random();
      if (Math.random() < 0.5) {
        variance = -variance;
      }
      let iconLeft = left;
      iconLeft += variance;
      icon.pos.left = iconLeft + '%';
      icon.pos.transform = 'rotate(' + Math.random() * 100 + 'deg)';
    }
  }

  pickProfileImage() {
    this.imageUpload?.destroySubscriptions();
    this.api
      .shared_User_retrieve()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (user: UserDTO) => {
          this.imageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.profilePicture'));
          this.imageUpload.imageTransfer.image = user?.avatar;
          this.imageUpload.subClippingChange = this.imageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.clippingChange(res);
          });
          this.imageUpload.subFileChange = this.imageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.fileChange(file);
          });
          this.imageUpload.subDeletePress = this.imageUpload.imageTransfer.deletePress.subscribe(() => {
            this.deletePress();
          });
          this.imageUpload.subModalClosed = this.imageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.uploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clippingChange(pClipping: Clipping) {
    if (pClipping) {
      this.applyClipping(pClipping);
      const transImage: FixedAspectRatioImageDTO = this.imageUpload.imageTransfer.image;
      this.imageUpload.imageTransfer.image = null;
      this.imageUpload.imageTransfer.uploading = true;
      this.api
        .shared_User_clipAvatar(transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
            this.imageUpload.imageTransfer.image = aspectRatioImage;
            this.imageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  applyClipping(pClipping: Clipping) {
    this.imageUpload.imageTransfer.image.clippingTLX = pClipping.clippingTLX;
    this.imageUpload.imageTransfer.image.clippingTLY = pClipping.clippingTLY;
    this.imageUpload.imageTransfer.image.clippingBRX = pClipping.clippingBRX;
    this.imageUpload.imageTransfer.image.clippingBRY = pClipping.clippingBRY;
  }

  fileChange(file: SingleFileUpload) {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;
    this.api
      .shared_User_uploadAvatar(file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
          this.imageUpload.imageTransfer.image = aspectRatioImage;
          this.imageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  deletePress() {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;
    this.api
      .shared_User_deleteAvatar()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.imageUpload.imageTransfer.image = null;
          }
          this.imageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  uploadModalClosed() {
    this.imageUpload.destroySubscriptions();
    this.refreshFromServer();
  }
}
